import { ApprovalPolicy, CreateApprovalPolicy } from '@/types/approvals'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useDeleteDetail, {
  UseDeleteDetailOptions,
} from '../core-hooks/useDeleteDetail'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import buildService from '../create-service'
import queryKeys from '../query-keys'

const service = buildService<ApprovalPolicy>({
  pathCategory: 'approval_policies',
})

export function useGetApprovalPolicies({
  filters,
  ...options
}: UseGetListOptions<ApprovalPolicy> = {}) {
  const query = useGetList<ApprovalPolicy>({
    filters,
    queryKey: queryKeys.approvalPolicies.list({ filters }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    approvalPolicies: query.allData,
    ...query,
  }
}

/**
 * One policy's live admin configuration — the trigger condition and order
 * rules behind it. A document's `approval_state` reports progress but not the
 * config it was derived from, so this is what a reader needs to see why a
 * policy applies to their document.
 */
export function useGetApprovalPolicy({
  id,
  ...options
}: UseGetDetailOptions<ApprovalPolicy>) {
  const query = useGetDetail<ApprovalPolicy>({
    id,
    queryKey: queryKeys.approvalPolicies.detail({
      id,
      filters: options.filters,
    }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    approvalPolicy: query.data,
    ...query,
  }
}

export function useCreateApprovalPolicy(
  options: UseCreateDetailOptions<CreateApprovalPolicy, ApprovalPolicy> = {},
) {
  const mutation = useCreateDetail<CreateApprovalPolicy, ApprovalPolicy>({
    serviceFn: service.createDetail,
    ...options,
  })

  return {
    createApprovalPolicy: mutation.mutate,
    createApprovalPolicyAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useUpdateApprovalPolicy(
  options: UseUpdateDetailOptions<ApprovalPolicy> = {},
) {
  const mutation = useUpdateDetail<ApprovalPolicy>({
    serviceFn: service.updateDetail,
    ...options,
  })

  return {
    updateApprovalPolicy: mutation.mutate,
    updateApprovalPolicyAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteApprovalPolicy(options: UseDeleteDetailOptions = {}) {
  const mutation = useDeleteDetail<ApprovalPolicy>({
    serviceFn: service.deleteDetail,
    ...options,
  })

  return {
    deleteApprovalPolicy: mutation.mutate,
    deleteApprovalPolicyAsync: mutation.mutateAsync,
    ...mutation,
  }
}
